<template>
    <div>
        <PageHeader>
            <span v-if="item">{{ item.name }}</span>
            <b-skeleton v-if="!item" animation="wave" width="45%" height="25px" class="mb-0" />
            <template #side>
                <strong>{{ $t('goBackTo') }}</strong>: <b-link :to="$route.params.parentUrl || parentRoute">
                    {{ $t(parentRoute.meta.title) }}
                </b-link>
            </template>
        </PageHeader>

        <div class="d-flex flex-wrap gap-4">
            <Card :header="$t('productDetails')" :loading="!item || loading" class="flex-basis-0 flex-grow-1 width-min-400 mb-auto">
                <FormValidator :validator="$v.form.name" form-group v-slot="slotProps" :label="$t('name')">
                    <b-form-input v-model="form.name" :disabled="busy" :state="slotProps.state" />
                </FormValidator>
                <FormValidator :validator="$v.form.url" form-group v-slot="slotProps" :label="$t('url')">
                    <b-form-input v-model="form.url" :disabled="busy" :state="slotProps.state" />
                </FormValidator>
                <FormValidator :validator="$v.form.description" form-group v-slot="slotProps" :label="$t('description')">
                    <b-form-textarea v-model="form.description" :disabled="busy" :state="slotProps.state" />
                </FormValidator>
                <FormValidator :validator="$v.form.type" form-group v-slot="slotProps" :label="$t('websiteType')">
                    <Multiselect
                        :value="form.type"
                        :fetch-options-function="fetchSpaceTypes"
                        track-by="value"
                        label="label"
                        :state="slotProps.state"
                        :disabled="busy"
                        @inputTrackBy="form.type = $event"
                    />
                </FormValidator>
                <b-form-group :label="$t('category')">
                    <Multiselect
                        :value="form.categories"
                        :fetch-options-function="fetchCategories"
                        track-by="id"
                        label="name"
                        :placeholder="$t('selectField.placeholder')"
                        :show-checkboxes="false"
                        :multiple="true"
                        :clear-on-select="false"
                        :close-on-select="false"
                        :show-selected="true"
                        :disabled="busy"
                        @inputTrackBy="form.categories = $event"
                    />
                </b-form-group>
                <b-form-group :label="$t('permissions')">
                    <Multiselect
                        :value="form.permissions"
                        :fetch-options-function="fetchSpacePermissions"
                        track-by="value"
                        label="label"
                        :placeholder="$t('selectField.placeholder')"
                        :disabled="busy"
                        :clear-on-select="false"
                        :close-on-select="false"
                        :multiple="true"
                        @inputTrackBy="form.permissions = $event"
                    />
                </b-form-group>
                <FormValidator :validator="$v.form.amazonTrackingId" form-group v-slot="slotProps" :label="$t('amazonTrackingId')">
                    <b-form-input v-model="form.amazonTrackingId" :disabled="busy" :state="slotProps.state" />
                </FormValidator>
                <FormValidator :validator="$v.form.gaApiSecret" form-group v-slot="slotProps" :label="$t('gaApiSecret')">
                    <b-form-input v-model="form.gaApiSecret" :disabled="busy" :state="slotProps.state" />
                </FormValidator>
                <FormValidator :validator="$v.form.gaSpace" form-group v-slot="slotProps" :label="$t('gaSpace')">
                    <b-form-input v-model="form.gaSpace" :disabled="busy" :state="slotProps.state" />
                </FormValidator>
                <FormValidator :validator="$v.form.productFeedUrl" form-group v-slot="slotProps" :label="$t('productFeedUrl')">
                    <b-form-input v-model="form.productFeedUrl" :disabled="busy" :state="slotProps.state" />
                </FormValidator>
                <div class="my-4"></div>
                <b-form-group class="mb-0">
                    <ButtonIcon class="m-auto wide" :loading="updateBusy" :disabled="!item || busy" @click="formSubmit()">
                        {{ $t('saveChanges') }}
                    </ButtonIcon>
                </b-form-group>
            </Card>

            <Card class="flex-basis-0 flex-grow-1 width-min-400 mb-auto">
                <SkeletonList :loading="!item">
                    <SkeletonItem :label="$t('ID')" width="80%">
                        <span v-if="item">{{ item.id }}</span>
                    </SkeletonItem>
                    <SkeletonItem :label="$t('dateAdded')" width="100%">
                        <span v-if="item">{{ $format.dateAndHour(item.createdAt, 'dd-MM-yyyy') }}</span>
                    </SkeletonItem>
                    <SkeletonItem :label="$t('user')" width="70%">
                        <span v-if="item">
                            <router-link :to="{ name: 'UsersDetails', params: { id: item.publisher.id } }">{{ item.publisher.name }}</router-link>
                            <span v-if="item.publisher.id"> ({{ item.publisher.id }}) </span> <span v-if="item.publisher.company">({{ item.publisher.company }})</span></span>
                    </SkeletonItem>
                    <SkeletonItem :label="$t('userType')" width="40%">
                        <span v-if="item">{{ roleTranslate(item.publisher.role) }}</span>
                    </SkeletonItem>
                    <SkeletonItem :label="$t('status')" width="30%">
                        <BadgeTransactionStatus v-if="item && item.status" :status="item.status" />
                    </SkeletonItem>
                </SkeletonList>

                <div v-if="item" class="d-flex gap-1">
                    <ButtonIcon
                        v-if="item.status == TransactionStatus.ACCEPTED || item.status == TransactionStatus.NEW"
                        variant="danger"
                        :disabled="busy"
                        :loading="rejectBusy"
                        @click="rejectSpace()"
                    >
                        {{ $t('reject') }}
                    </ButtonIcon>
                    <ButtonIcon
                        v-if="item.status == TransactionStatus.REJECTED || item.status == TransactionStatus.NEW"
                        variant="success"
                        :disabled="busy"
                        :loading="acceptBusy"
                        @click="acceptSpace()"
                    >
                        {{ $t('accept') }}
                    </ButtonIcon>
                </div>
            </Card>
        </div>
    </div>
</template>

<script>
    import cloneDeep from 'lodash/cloneDeep';
    import { required, or, not } from 'vuelidate/lib/validators';
    import { url } from '@/utils/validators';
    import { roleTranslate } from '@/Role';
    import { TransactionStatus } from '@/services/transactionService';
    import PageHeader from '@/components/common/PageHeader.vue';
    import FormValidator from '@/components/common/FormValidator.vue';
    import Multiselect from '@/components/common/Multiselect.vue';
    import BadgeTransactionStatus from '@/components/common/BadgeTransactionStatus.vue';
    import SkeletonList from '@/components/common/SkeletonList.vue';
    import SkeletonItem from '@/components/common/SkeletonItem.vue';
    import { deepMergeWithFilter } from '@/utils/util';
    import formBackendValidationMixin from '@/mixins/formBackendValidationMixin';

    const FORM_DEFAULT_DATA = Object.freeze({
        name: '',
        url: '',
        description: '',
        type: '',
        categories: '',
        permissions: '',
        amazonTrackingId: '',
        gaApiSecret: '',
        gaSpace: '',
        productFeedUrl: '',
    });

    export default {
        name: 'WebpagesDetails',
        mixins: [formBackendValidationMixin],
        components: {
            PageHeader,
            FormValidator,
            Multiselect,
            BadgeTransactionStatus,
            SkeletonList,
            SkeletonItem,
        },
        data() {
            return {
                requestSpace: this.$req('space/space'),
                requestSpaceAccept: this.$req('space/spaceAccept'),
                requestSpaceReject: this.$req('space/spaceReject'),
                requestSpaceTypes: this.$req('space/spaceTypes'),
                requestSpacePermissions: this.$req('space/SpacePermissions'),
                requestCategories: this.$req('category/buyboxCategories'),
                id: Number.parseInt(this.$route.params.id),
                form: cloneDeep(FORM_DEFAULT_DATA),
                formValidations: {
                    form: {
                        name: { required },
                        url: { required, url },
                        description: { required },
                        type: { required },
                        amazonTrackingId: {},
                        gaApiSecret: {},
                        gaSpace: {},
                        productFeedUrl: { url: or(url, not(required)) },
                    },
                },
                item: null,
                TransactionStatus,
            };
        },
        computed: {
            items() {
                return Object.values(this.$store.state.space.spaces || {});
            },
            parentRoute() {
                return this.$route.matched.slice(-2)[0];
            },
            busy() {
                return this.loading || this.updateBusy || this.acceptBusy || this.rejectBusy;
            },
            loading() {
                return this.requestSpace.getLoading();
            },
            updateBusy() {
                return this.requestSpace.getLoading('update');
            },
            acceptBusy() {
                return this.requestSpaceAccept.getLoading();
            },
            rejectBusy() {
                return this.requestSpaceReject.getLoading();
            },
        },
        watch: {
            item: {
                handler: 'initFormData',
            },
        },
        methods: {
            roleTranslate,
            updateItem(item) {
                this.item = { ...this.item, ...item };

                // After removing the already set value, api doesnt return empty value and because of this merging with old form the value remains unchanged
                if (!item.productFeedUrl) {
                    delete this.item.productFeedUrl;
                }
            },
            fetchSpace() {
                return this.requestSpace
                    .get(this.id)
                    .then(this.updateItem)
                    .catch(error => {
                        this.$toastr.error(error.message, this.$t('errorOccurred'));
                        this.$router.push(this.parentRoute);
                    });
            },
            fetchSpaceTypes() {
                return this.requestSpaceTypes.get().then(data => data.items);
            },
            fetchSpacePermissions() {
                return this.requestSpacePermissions.get();
            },
            fetchCategories() {
                return this.requestCategories.get();
            },
            initFormData() {
                const form = deepMergeWithFilter(FORM_DEFAULT_DATA, this.item);
                form.categories = this.item.categories?.map(item => item.id) || [];
                this.form = form;
            },
            formSubmit() {
                this.$v.$touch();
                if (this.$v.$invalid) return;
                this.requestSpace.id('update').patch(this.id, this.form).then(this.updateItem).catch(this.handleBackendValidations);
            },
            acceptSpace() {
                this.requestSpaceAccept
                    .put(this.id)
                    .then(this.updateItem)
                    .catch(error => this.$toastr.error(error.message, this.$t('errorOccurred')));
            },
            rejectSpace() {
                this.requestSpaceReject
                    .put(this.id)
                    .then(this.updateItem)
                    .catch(error => this.$toastr.error(error.message, this.$t('errorOccurred')));
            },
        },
        created() {
            this.fetchSpace();
        },
    };
</script>
